import { useEffect, useState } from 'react';

import {
    Box,
    Button,
    Card,
    Divider,
    Flex,
    Heading,
    Icon,
    Link,
    List,
    ListItem,
    Text,
    UnorderedList,
    useBreakpointValue
} from '@chakra-ui/react';

import moment from 'moment';
import 'moment-timezone';

import { DonationDisplay } from './Misc/DonationDisplay';
import { SetupBox } from './Misc/SetupBox';

import { FaUserEdit, FaMusic } from "react-icons/fa";
import { FaRegCalendar, FaRegCalendarPlus } from "react-icons/fa6";
import { FiExternalLink } from "react-icons/fi";
import { TbLocation } from "react-icons/tb";

export function EventInfo({eventConfig, sessionActive, slotsRemaining, changePage, setups, regTotal, regAllowed, schedAllowed}) {

    const [SetupGrid, setSetupGrid] = useState([]);
    const [eventStartDayStr, setEventStartDayStr] = useState("");
    const [eventEndDayStr, setEventEndDayStr] = useState("");
    const [eventYear, setEventYear] = useState("");
    const [eventTimeItems, setEventTimeItems] = useState([]);

    const srpgNum = eventConfig["srpgNum"];
    const eventShort = eventConfig["eventShort"];
    const liveLong = eventConfig["liveLong"];
    const eventOver = eventConfig["eventOver"];
    const doubleSlotCutoff = eventConfig["doubleSlotCutoff"];

    const regButtonShown = !eventOver && sessionActive & (regAllowed);
    const schedButtonShown = !eventOver && sessionActive & (schedAllowed);

    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );

    const SignUpDisplay = () => {
        if (!eventOver && (!sessionActive || regButtonShown || schedButtonShown)) {
            return (
                <Card padding={isMobile()? 3 : 8} mb={4}>   
                    <Heading size="md" mb={4}>
                        How do I sign up for {liveLong}?
                    </Heading>
                    <Divider mb={4}/>                                     
                    <SchedButtonsText/>
                    <Box textAlign="center">
                        <RegisterButton/>
                        <ScheduleButton/>
                    </Box>
                </Card>
            );
        }
        return ([]);
    }

    const SchedButtonsText = () => {
        if(sessionActive) {
            if(regButtonShown) {
                return (
                    <Card bgColor="whiteAlpha.300" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Registration
                        </Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            If you want to compete, you can register and then schedule a set-- or if you want to just come to hang out, you can attend as a 
                            spectator.
                        </Text>
                    </Card>
                );
            }
            if(schedButtonShown) {
                let slotStr = "a set"

                if (slotsRemaining > 1) {
                    slotStr = `${slotsRemaining} sets`
                }

                return (
                    <Card bgColor="whiteAlpha.300" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Set Scheduling
                        </Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            Now that you've registered as a competitor, you can schedule {slotStr}.
                        </Text>
                    </Card>
                );
            }            
            return([]);
        }
        return (
            <Card bgColor="whiteAlpha.300" padding={isMobile()? 3 : 8} mb={4}>
                <Heading size="md" mb={4}>
                    Registration
                </Heading>
                <Divider mb={4}/>
                <Text mb={4}>
                    In order to sign up to attend, you'll need to do the following:
                </Text>
                <UnorderedList ml={isMobile() ? 8 : 12} mb={4}>
                    <ListItem><Link color='teal.300' href={`https://groovestats.com/index.php?page=register`} isExternal>Register for a GrooveStats account <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/></Link></ListItem>
                    <ListItem><Link color='teal.300' onClick={()=>{changePage("log-in")}}>Log in on this website</Link></ListItem>
                    <ListItem>You can then choose to schedule a time slot for your set or register as a spectator</ListItem>
                </UnorderedList>
            </Card>
        );
    };

    const RegisterButton = () => {
        if(regButtonShown) {
            return (
                <Button width={{base: "250px", xl: "600px"}} isDisabled={!regAllowed} flex="1" onClick={()=>{changePage("register")}}>
                    <Icon as={FaUserEdit} boxSize={5} mr={2}/> Register For Event
                </Button>
            );
        }
        return([]);
    }

    const ScheduleButton = () => {
        if(schedButtonShown) {
            return (
                <Button width={{base: "250px", xl: "600px"}} isDisabled={!schedAllowed} flex="1" onClick={()=>{changePage("sched-set")}}>
                    <Icon as={FaRegCalendarPlus} boxSize={5} mr={2}/> Schedule Set
                </Button>
            );
        }
        return([]);
    }    

    useEffect(()=>{
        const newSetupGrid = Object.values(setups)?.map((setupId, idx)=>
            <SetupBox
                setups={setupId} 
                key={idx}
            />
        );
        setSetupGrid(newSetupGrid);
    }, [setups]);

    useEffect(()=>{
        const eventDates = eventConfig?.eventDates;

        const newEventTimes = [];

        eventDates.forEach((eventDate, idx)=>{
            const startDateTime = moment.tz(`${eventDate["date"]} ${eventDate["start"]}`, "America/New_York");
            const endDateTime = moment.tz(`${eventDate["date"]} ${eventDate["end"]}`, "America/New_York");

            const startWeekday = startDateTime.format("dddd");

            const startTime = startDateTime.format("h:mm a");
            const endTime = endDateTime.format("h:mm a");

            if (idx === 0) {
                const newEventStartDayStr = startDateTime.format("MMMM Do");
                const newEventYear = startDateTime.format("YYYY");
                setEventStartDayStr(newEventStartDayStr);
                setEventYear(newEventYear);
            }

            if (idx === (eventDates.length -1)) {
                const newEventEndDayStr = endDateTime.format("MMMM Do");
                setEventEndDayStr(newEventEndDayStr);
            }

            newEventTimes.push({
                startWeekday: startWeekday,
                startTime: startTime,
                endTime: endTime
            })
        });

        const newTimeListItems = newEventTimes?.map((time, idx)=>
            <ListItem key={idx}><strong>{time.startWeekday}:</strong> {time.startTime} - {time.endTime}</ListItem>
        );

        setEventTimeItems(newTimeListItems);
    }, [eventConfig]);

    try {
        return (
            <Box width="100%">
                <Text mb={4}>
                    Six years after the original Stamina Con, I'm happy to announce the next event in the series: {liveLong}!
                </Text>
                <Text mb={4}>
                    Join us for a weekend of stamina, friendship, and fun diversions on the side!
                </Text>
                <Flex flexFlow="wrap" justifyContent="center" flexDirection="row" gap={3}>
                    <Card flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            When is {liveLong}?
                        </Heading>
                        <Divider mb={4}/>
                        <Heading color="blue.200" size="md" mb={4}>
                            {eventStartDayStr}-{eventEndDayStr} {eventYear}
                        </Heading>
                        <Text mb={4}>
                            The space will open the following hours:
                        </Text>
                        <List>
                            {eventTimeItems}
                        </List>
                    </Card>
                    <Card flex="2" flexBasis="375px" padding={isMobile()? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Where is {liveLong}?
                        </Heading>
                        <Divider mb={4}/>
                        <Heading color="blue.200" size="md" mb={4}>
                            PGH Dance Game Space<br/>
                            Pittsburgh, PA
                        </Heading>
                        <Text mb={4}>
                            You can find more information on the venue by visiting the venue info page:
                        </Text>
                        <Flex>
                            <Button flex="1" mb={4} onClick={()=>{changePage("venue-info")}}>
                                <Icon as={TbLocation} boxSize={5} mr={2}/> Venue Info
                            </Button>
                        </Flex>
                    </Card>
                </Flex>
                <Card padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        What is {liveLong}?
                    </Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>Here's what's going on at <strong>{liveLong}</strong>:</Text>
                    <Flex flexFlow="wrap" justifyContent="center" flexDirection="row" gap={3}>
                        <Card bgColor="whiteAlpha.300" flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                            <Heading size="md" mb={4}>
                                Main Tournaments:
                            </Heading>
                            <Divider mb={4}/>                            
                            <UnorderedList mb={4} style={{fontWeight: "bold"}} ml={isMobile() ? 8 : 12}>
                                <ListItem><Heading color="blue.200" size="md">ITG Singles</Heading></ListItem>
                                <ListItem><Heading color="blue.200" size="md">ITG Doubles</Heading></ListItem>
                                <ListItem><Heading color="blue.200" size="md">Pump Singles</Heading></ListItem>
                                <ListItem><Heading color="blue.200" size="md">Pump Doubles</Heading></ListItem>
                            </UnorderedList>    
                            <Text mb={4}>
                                {liveLong} centers around a competition using the songlist from Stamina RPG {srpgNum}.  Entrants will be aiming to
                                get highest sum of TPX and LPX they can.  Only your <strong>best LPX pass</strong> and <strong>five best TPX passes</strong> contribute towards
                                your total-- imagine TP/LP from SRPG, but with scoring as a component.  
                            </Text>
                            <Text mb={4}>Charts converted from ITG Singles will be used for ITG Doubles and Pump Singles/Doubles.</Text>
                            <Text mb={4}>
                                You can view the songlist and point values on the Song Data page:
                            </Text>
                            <Flex>
                                <Button ml="auto" mr="auto" mb={4} width={{base: "250px", xl: "600px"}} onClick={()=>{changePage("song-data")}}>
                                    <Icon as={FaMusic} boxSize={5} mr={2}/> Song Data
                                </Button>
                            </Flex>                    
                            <Text mb={4}>
                                ... and you can read up on Stamina RPG {srpgNum} here:
                            </Text>
                            <Text mb={4}>
                                <Link color='teal.300' href={`https://${eventShort}.groovestats.com/`} isExternal>
                                    Stamina RPG {srpgNum} Info/Rules <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/>
                                </Link>
                            </Text>      
                            <Text mb={4}>
                                Keep reading below to learn more about set scheduling and the machine setups that will be available!
                            </Text>                                                                      
                        </Card>
                        <Card bgColor="whiteAlpha.300" flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                            <Heading size="md" mb={4}>
                                Side Events:
                            </Heading>
                            <Divider mb={4}/>                            
                            <UnorderedList ml={isMobile() ? 8 : 12}>
                                <ListItem>
                                    <Heading color="blue.200" size="xs">Stamina Radio LIVE Rave</Heading> 
                                    <Text mb={2}>
                                        The flagship Stamina Con side event, featuring Rems, the legendary stamina DJ responsible for 
                                        mixing 18 of the 24 mainline East Coast Stamina marathons (as well as a bunch of others).
                                    </Text>
                                    <Text mb={2}>Anyone who came to Stamina Con previously can tell you attending 
                                        this one is practically a requirement!
                                    </Text>
                                </ListItem>
                                <ListItem>
                                    <Heading color="blue.200" size="xs">Halloween Costume Contest</Heading> 
                                    <Text mb={2}>
                                        Suit up in your costume-- Archi will be the one to decide who's worthy!
                                    </Text>
                                    <Text mb={2}>
                                        The winner will be chosen based on craftmanship, creativity, and how 
                                        much <strong>STAMINA</strong> your costume can endure.  Good luck and have fun!
                                    </Text>
                                </ListItem>                            
                                <ListItem>
                                    <Heading color="blue.200" size="xs">Pumpkin Hunt</Heading> 
                                    <Text mb={2}>
                                        Keep your eyes peeled, pumpkins are hiding everywhere around the con!
                                    </Text>
                                    <Text mb={2}>
                                        Find as many as you can and claim your prize.  You never know where one might be lurking!
                                    </Text>
                                </ListItem>                 
                                <ListItem>
                                    <Heading color="blue.200" size="xs">Board Games</Heading> 
                                    <Text mb={2}>
                                        Join us for some board game sessions packed with variety: Classics, party games, and more!  
                                        Whether you're into strategy, chaoas, or laughs, there's something for everyone.
                                    </Text>
                                    <Text mb={2}>
                                        Feel free to bring your own game and join the fun!
                                    </Text>
                                </ListItem>                                            
                                <ListItem>
                                    <Heading color="blue.200" size="xs">Group Picture</Heading> 
                                    <Text mb={2}>
                                        I'm just putting this one here so that people are aware it's happening!  
                                    </Text>
                                </ListItem>  
                            </UnorderedList>                        
                            <Text>                                    
                                Check the <Link color='teal.300' onClick={()=>{changePage("calendar")}}>schedule</Link> for the times
                                for all of these!
                            </Text>                            
                        </Card>                    
                    </Flex> 
                </Card>                                       
                <Card flex="2" flexBasis="375px" padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Tournament Setups and Scheduling
                    </Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>
                        At the event, if you're competing, you have the opportunity to select a setup to do your set(s) on.
                        These mostly include DDR/ITG pads, and if you select a setup where
                        you're allowed to swap the pads out, you can bring whatever full sized pad you want.
                    </Text>
                    <Text mb={4}>
                        For <strong>travel/mini pads only</strong> (please check with us on 
                        <Link color='teal.300' href={'https://staminanation.com/'} isExternal> Discord <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/></Link> 
                        to make sure yours counts), you may bring and use the pad on <em>any</em> setup-- including the 'non-swappable' ones--
                        provided you bring a non-slip mat to place between your pad and the surface of the setup's default pad to avoid 
                        any damage.
                    </Text>
                    <Text mb={4}>
                        Note that after you've checked in at the event, <strong>scores obtained at any time on any setup will count towards your total</strong>.
                        However, during your set(s), you have guaranteed access to the setup you chose, so it is to your advantage to schedule one.
                    </Text>
                    <Text mb={4}>
                        Here are details on the different options for setups:
                    </Text>
                    <Flex mt={3} flexDirection="row" flexFlow="wrap" justifyContent={"center"}>
                        {SetupGrid}    
                    </Flex>
                    <Text mb={4}>
                        All entrants are allowed a 3 hour block on the schedule for their sets.  Additionally, anyone 
                        entering that meets or exceeds <strong>{doubleSlotCutoff}</strong> TP+LP from SRPG{srpgNum} can 
                        register a <strong>SECOND</strong> time slot.
                    </Text>                    
                    <Text mb={4}>
                        Outside of the core time slots for competing, the machines will be free to use for
                        anyone who wants to play.
                    </Text>
                    <Text mb={4}>
                        You can see what times the various entrants are playing by checking the calendar below:
                    </Text>
                    <Box textAlign="center">
                        <Button width={{base: "250px", xl: "600px"}} onClick={()=>{changePage("calendar")}}>
                            <Icon as={FaRegCalendar} boxSize={5} mr={2}/> Set Calendar/Schedule
                        </Button>
                    </Box>
                </Card>
                <SignUpDisplay/>
                <Card padding={isMobile() ? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Who/Why is {liveLong}?
                    </Heading>
                    <Divider mb={4}/>
                    <Box alignContent="space-around">
                        <Heading align="center" size="md" mb={4}>
                            Currently,
                        </Heading>
                        <Heading align="center" size="xl" mb={4}>{regTotal} / {eventConfig["maxPlayerCount"]}</Heading>
                        <Heading align="center" size="md" mb={4}>
                            registration slots have been filled.
                        </Heading>
                    </Box>                    
                    <Text mb={4}>
                        I want to give special mention to the following people for contributing to this event and helping to make it possible:
                    </Text>
                    <UnorderedList ml={isMobile() ? 8 : 12}>
                        <ListItem><strong>quietly-turning - </strong> for his marvelous work building the PGH Dance Game Space, and for letting us host another event there!</ListItem>
                        <ListItem><strong>Pluto - </strong> for the spooky and excellent logo design!</ListItem>
                        <ListItem><strong>teejusb - </strong> for doing the theme for the event!</ListItem>
                        <ListItem><strong>Rems - </strong> for making the time to come out to the event and put on another show for us!</ListItem>
                        <ListItem><strong>Xiwangz - </strong> for planning and organizing all the non-rave side events and activities!</ListItem>
                        <ListItem>... and <strong>everyone who registered to come - </strong> we can't run an event like this if people don't want to attend, so thank you for being a part of everything!</ListItem>
                    </UnorderedList>
                </Card>
                <Card padding={isMobile() ? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Contributing
                    </Heading>     
                    <Divider mb={4}/>                                           
                    <DonationDisplay/>
                </Card>

            </Box>
        );
    } catch (e) {
        console.error("EventInfo render error: ", e);
    }
}